import { captureException } from '@/utils/sentry';
import { SalesPortalManagerPipelineSummaryDocument } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/documents/sales-portal-manager-pipeline-summary.generated';
import { SalesPortalManagerProspectForecastDocument } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/documents/sales-portal-manager-prospect-forecast.generated';
import { SalesPortalManagerProspectsDocument } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/documents/sales-portal-manager-prospects.generated';
import { SalesPortalRepViewProspectsDocument } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/documents/sales-portal-rep-view-prospects.generated';
import { useApolloClient } from '@apollo/client';
import { useSalesPortalDeactivateProspectMutation } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/blocs/documents/sales-portal-deactivate-prospect.generated';

const PROSPECT_LIST_QUERIES = [
  SalesPortalManagerPipelineSummaryDocument,
  SalesPortalManagerProspectForecastDocument,
  SalesPortalManagerProspectsDocument,
  SalesPortalRepViewProspectsDocument,
];

interface DeactivateProspectMutatorParams {
  restaurantUUID: string;
  supplierUUID: string;
}

interface DeactivateProspectMutatorResult {
  deactivateProspect: () => Promise<void>;
  loading: boolean;
}

/**
 * Mutator to deactivate a prospect when a rep marks it as lost. After a
 * successful deactivation, it refetches the prospect lists and manager
 * aggregates. A refetch failure goes to Sentry and does not fail the
 * deactivation.
 *
 * @param params.restaurantUUID - Restaurant of the prospect to deactivate.
 * @param params.supplierUUID - Supplier that owns the prospect.
 * @returns {@link DeactivateProspectMutatorResult}. `deactivateProspect`
 * rejects when the mutation fails or the backend does not report success.
 */
export const useDeactivateProspectMutator = ({
  restaurantUUID,
  supplierUUID,
}: DeactivateProspectMutatorParams): DeactivateProspectMutatorResult => {
  const apolloClient = useApolloClient();
  const [deactivateProspectMutation, { loading }] =
    useSalesPortalDeactivateProspectMutation();

  const deactivateProspect = async () => {
    const { data, errors } = await deactivateProspectMutation({
      variables: { restaurantUUID, supplierUUID },
    });
    if (!data?.supplierDeactivateRestaurant.success) {
      throw new Error('supplierDeactivateRestaurant did not succeed', {
        cause: errors,
      });
    }
    try {
      await apolloClient.refetchQueries({ include: PROSPECT_LIST_QUERIES });
    } catch (refetchError) {
      captureException(refetchError);
    }
  };

  return { deactivateProspect, loading };
};
